/**
 * Sample Notifications Data
 * Version: 6.2.0
 * 
 * Demo data for notifications system
 */
import {
  Notification,
  NotificationCategory,
  NotificationPriority,
  createNotification,
} from '../types/notifications'

// Generate sample notifications
export const sampleNotifications: Notification[] = [
  // Trade Notifications
  createNotification({
    title: 'Order Executed',
    message: 'BUY 50 NIFTY 24500 CE @ ₹245 executed successfully',
    category: NotificationCategory.TRADE,
    priority: NotificationPriority.NORMAL,
    timestamp: new Date(Date.now() - 5 * 60000),
    data: {
      orderId: 'ORD-2024-001234',
      symbol: 'NIFTY',
      type: 'BUY',
      quantity: 50,
      price: 245,
      pnl: 1250,
      botName: 'Breakout Bot',
    }
  }),
  createNotification({
    title: 'Stop Loss Triggered',
    message: 'SELL 100 BANKNIFTY @ ₹48500 - Stop loss hit',
    category: NotificationCategory.TRADE,
    priority: NotificationPriority.HIGH,
    timestamp: new Date(Date.now() - 30 * 60000),
    data: {
      orderId: 'ORD-2024-001233',
      symbol: 'BANKNIFTY',
      type: 'SELL',
      quantity: 100,
      price: 48500,
      pnl: -2500,
      botName: 'RSI Reversal',
    }
  }),
  createNotification({
    title: 'Profit Target Reached',
    message: 'NIFTY 24600 target achieved! +₹5,800 profit',
    category: NotificationCategory.TRADE,
    priority: NotificationPriority.HIGH,
    timestamp: new Date(Date.now() - 45 * 60000),
    data: {
      orderId: 'ORD-2024-001232',
      symbol: 'NIFTY',
      type: 'SELL',
      quantity: 50,
      price: 24600,
      pnl: 5800,
      botName: 'Trend Follower',
    }
  }),
  // Alert Notifications
  createNotification({
    title: 'Price Alert: NIFTY',
    message: 'NIFTY crossed above 24500 resistance level',
    category: NotificationCategory.ALERT,
    priority: NotificationPriority.NORMAL,
    timestamp: new Date(Date.now() - 2 * 60 * 60000),
    data: {
      alertType: 'price',
      symbol: 'NIFTY',
      condition: 'Crosses above',
      currentValue: 24523,
      threshold: 24500,
    }
  }),
  createNotification({
    title: 'Volume Spike Alert',
    message: 'BANKNIFTY volume 3x average in last 15 minutes',
    category: NotificationCategory.ALERT,
    priority: NotificationPriority.HIGH,
    timestamp: new Date(Date.now() - 3 * 60 * 60000),
    data: {
      alertType: 'volume',
      symbol: 'BANKNIFTY',
      condition: 'Volume > 3x average',
      currentValue: 1500000,
      threshold: 500000,
    }
  }),
  // System Notifications
  createNotification({
    title: 'System Maintenance',
    message: 'Scheduled maintenance on Saturday 10 PM - 2 AM',
    category: NotificationCategory.SYSTEM,
    priority: NotificationPriority.NORMAL,
    timestamp: new Date(Date.now() - 24 * 60 * 60000),
    data: {
      systemType: 'maintenance',
      downtime: new Date(Date.now() + 3 * 24 * 60 * 60000),
    }
  }),
  // Bot Notifications
  createNotification({
    title: 'Bot Started',
    message: 'Breakout Bot resumed trading operations',
    category: NotificationCategory.BOT,
    priority: NotificationPriority.NORMAL,
    timestamp: new Date(Date.now() - 6 * 60 * 60000),
    data: {
      botId: 'bot-001',
      botName: 'Breakout Bot',
      eventType: 'started',
    }
  }),
  createNotification({
    title: 'Bot Error',
    message: 'Trend Follower encountered API rate limit',
    category: NotificationCategory.BOT,
    priority: NotificationPriority.URGENT,
    timestamp: new Date(Date.now() - 2 * 60 * 60000),
    data: {
      botId: 'bot-003',
      botName: 'Trend Follower',
      eventType: 'error',
      reason: 'API rate limit exceeded',
    }
  }),
  // Portfolio Notifications
  createNotification({
    title: 'Daily P&L Update',
    message: 'Your portfolio gained +₹12,450 today (+2.3%)',
    category: NotificationCategory.PORTFOLIO,
    priority: NotificationPriority.NORMAL,
    timestamp: new Date(Date.now() - 30 * 60000),
    data: {
      metricType: 'pnl',
      currentValue: 12500,
      changeValue: 12450,
      changePercent: 2.3,
    }
  }),
  createNotification({
    title: 'Margin Alert',
    message: 'Available margin dropped below ₹50,000',
    category: NotificationCategory.PORTFOLIO,
    priority: NotificationPriority.HIGH,
    timestamp: new Date(Date.now() - 45 * 60000),
    data: {
      metricType: 'margin',
      currentValue: 45000,
      changeValue: -5000,
      changePercent: -10,
      threshold: 50000,
    }
  }),
  // Market Notifications
  createNotification({
    title: 'Market Open',
    message: 'NSE trading session started. Happy trading!',
    category: NotificationCategory.MARKET,
    priority: NotificationPriority.NORMAL,
    timestamp: new Date(Date.now() - 9 * 60 * 60000),
    data: {
      marketType: 'market_open',
      description: 'NSE trading session started',
      affectedSegments: ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'SENSEX'],
    }
  }),
]

// Get unread count
export function getUnreadCount(notifications: Notification[]): number {
  return notifications.filter(n => n.status !== 'archived').length
}

// Get high priority notifications
export function getHighPriorityNotifications(notifications: Notification[]): Notification[] {
  return notifications.filter(
    n => n.priority === NotificationPriority.HIGH || n.priority === NotificationPriority.URGENT
  )
}